import {
    DeserializeCallbackFunc,
    DeserializerFunc1,
    IDeserializeContext,
    IModelSchema,
    IPropSchema,
    PropSchema
} from '@mintdata/mint-kit/impl/util/serializr/src/api/types';
import {getDefaultModelSchema} from '../api/getDefaultModelSchema';
import {_defaultPrimitiveProp, SKIP} from '../constants';
/*
 * Deserialization
 */
import {GUARDED_NOOP, invariant, isModelSchema, isPrimitive, parallel} from '../utils/utils';
import {Context} from './Context';

function schemaHasAlias(schema:IModelSchema, name:string):boolean
{
    for( const key in schema.props )
        if( typeof schema.props[key] === 'object' && (schema.props[key] as IPropSchema).jsonname === name )
            return true;

    return false;
}

function deserializeStarProps(schema:IModelSchema, obj:Object, json:any, baseValue:any):void
{
    for( const key in json )
        if( !(key in schema.props) && !schemaHasAlias(schema, key) )
        {
            const value:any = json.hasOwnProperty(key) ? json[key] : baseValue[key];

            // when deserializing we don't want to silently ignore 'unparsable data' to avoid confusing bugs
            invariant(isPrimitive(value), 'encountered non primitive value while deserializing \'*\' properties in property \'' + key + '\': ' + value);

            obj[key] = value;
        }
}

/**
 * Deserializes a json structor into an object graph.
 * This process might be asynchronous (for example if there are references with an asynchronous
 * lookup function). The function returns an object (or array of objects), but the returned object
 * might be incomplete until the callback has fired as well (which might happen immediately)
 *
 * @param {object|array} schema to use for deserialization
 * @param {json} json data to deserialize
 * @param {function} callback node style callback that is invoked once the deserializaiton has finished.
 * First argument is the optional error, second argument is the deserialized object (same as the return value)
 * @param {*} customArgs custom arguments that are available as `context.args` during the deserialization process. This can be used as dependency injection mechanism to pass in, for example, stores.
 * @returns {object|array} deserialized object, possibly incomplete.
 */
export function deserialize(schema:IModelSchema | Object, json:any, callback?:DeserializeCallbackFunc, customArgs?:Array<any>):any
{
    invariant(arguments.length >= 2, 'deserialize expects at least 2 arguments');

    schema = getDefaultModelSchema(schema);

    invariant(isModelSchema(schema), 'first argument should be model schema');

    if( Array.isArray(json) )
    {
        const items:Array<any> = [];

        parallel(
            json,
            function(childJson:any, itemDone:DeserializeCallbackFunc):void
            {
                const instance = deserializeObjectWithSchema(null, schema as IModelSchema, childJson, itemDone, customArgs);

                // instance is created synchronously so can be pushed
                items.push(instance);
            },
            callback || GUARDED_NOOP
        );

        return items;
    }
    else
        return deserializeObjectWithSchema(null, schema as IModelSchema, json, callback, customArgs);
}

export function deserializeDiff(schema:IModelSchema | Object, json:any, baseValue:any, callback?:DeserializeCallbackFunc, customArgs?:Array<any>):any
{
    invariant(arguments.length >= 3, 'deserialize expects at least 3 arguments');

    schema = getDefaultModelSchema(schema);

    invariant(isModelSchema(schema), 'first argument should be model schema');

    if( Array.isArray(json) )
    {
        const items:Array<any> = [];

        parallel(
            json,
            function(childJson:any, itemDone:DeserializeCallbackFunc, index:number):void
            {
                let instance;

                if( childJson === SKIP )
                {
                    const AEmpty = require('@mintdata/mint-kit/api/values/AEmpty').AEmpty;

                    instance = baseValue && baseValue[index] ? baseValue[index] : AEmpty.Value;
                }
                else
                    instance = deserializeObjectWithSchema(
                        null,
                        schema as IModelSchema,
                        childJson,
                        itemDone,
                        customArgs,
                        baseValue ? baseValue[index] : null
                    );

                // instance is created synchronously so can be pushed
                items.push(instance);
            },
            callback || GUARDED_NOOP
        );

        return items;
    }
    else
        return deserializeObjectWithSchema(
            null,
            schema as IModelSchema,
            json,
            callback,
            customArgs,
            baseValue
        );
}

export function deserializeObjectWithSchema(parentContext:IDeserializeContext,
                                            schema:IModelSchema,
                                            json:any,
                                            callback:DeserializeCallbackFunc,
                                            customArgs:Array<any> = null,
                                            baseValue:any = null):any
{
    if( json === null || json === undefined )
        return callback(null, null);

    const context:IDeserializeContext = new Context(parentContext, schema, json, callback, customArgs);
    const target:any = schema.factory(context);

    // todo async invariant
    invariant(!!target, 'No object returned from factory');
    // TODO: make invariant?
    //  invariant(schema.extends || !target.constructor.prototype.constructor.serializeInfo, "object has a serializable supertype, but modelschema did not provide extends clause")

    context.target = target;

    const lock:Function = context.createCallback(GUARDED_NOOP);

    deserializePropsWithSchema(context, schema, json, target, baseValue);

    lock();

    return target;
}

export function deserializePropsWithSchema(context:IDeserializeContext,
                                           schema:IModelSchema,
                                           json:any,
                                           target:any,
                                           baseValue:any = null):any
{
    if( schema.extends )
        deserializePropsWithSchema(context, schema.extends, json, target, baseValue);

    Object.keys(schema.props).forEach(function(propName:string)
    {
        let propDef:PropSchema = schema.props[propName];

        if( propName === '*' )
        {
            invariant(propDef === true, 'prop schema \'*\' can only be used with \'true\'');

            deserializeStarProps(schema, target, json, baseValue);

            return;
        }

        if( propDef === true )
            propDef = _defaultPrimitiveProp;

        if( propDef === false )
            return;

        // NOTE: Custom mint code start
        const jsonPropName:string = propDef.prevname && json[propDef.prevname] ? propDef.prevname : propName;
        // NOTE: Custom mint code end
        const jsonAttr:string = propDef.jsonname || jsonPropName;

        if( !json.hasOwnProperty(jsonAttr) )
        {
            if( baseValue && baseValue.hasOwnProperty(propName) )
                target[propName] = baseValue[propName];

            return;
        }

        (propDef.deserializer as DeserializerFunc1)(
            json[jsonAttr],
            // for individual props, use root context based callbacks
            // this allows props to complete after completing the object itself
            // enabling reference resolving and such
            context.rootContext.createCallback(function(value)
            {
                if( value !== SKIP )
                {
                    // NOTE: Custom mint code start
                    if( (propDef as IPropSchema).converter && propName != jsonPropName )
                        target[propName] = (propDef as IPropSchema).converter(value);
                    else
                        // NOTE: Custom mint code end
                        target[propName] = value;
                }
            }) as DeserializeCallbackFunc,
            context,
            target[propName], // initial value
            baseValue && baseValue[propName]
        );
    });
}
